import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  TextField,
  useTheme
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Business, AdminPanelSettings, Search, CheckCircle, Error as ErrorIcon, Visibility } from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import { ResponsiveTable, ResponsiveTableColumn, ResponsiveTableRow } from '@components/common/ResponsiveTable/ResponsiveTable';
import { useOrgAdminStore } from '@store/orgAdminStore';
import { OrgAdmin, OrganizationDomain } from '@services/OrgAdminService';

// Extend OrgAdmin to be compatible with ResponsiveTable
interface OrgAdminRow extends Omit<OrgAdmin, 'id'>, ResponsiveTableRow {
  user_id: string;
  organization_name: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  domains?: OrganizationDomain[];
}

const ManageOrganizationalAdmins: React.FC = () => {
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();

  // Zustand store
  const {
    admins,
    loading,
    error,
    success,
    fetchAllAdmins,
    deleteAdmin,
    clearMessages,
  } = useOrgAdminStore();

  // Local state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<OrgAdminRow | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAdmins, setFilteredAdmins] = useState<OrgAdminRow[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [messageDialog, setMessageDialog] = useState(false);

  // Load data on mount
  useEffect(() => {
    fetchAllAdmins();
  }, [fetchAllAdmins]);

  // Filter admins based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAdmins(admins as OrgAdminRow[]);
    } else {
      const searchLower = searchTerm.toLowerCase();
      setFilteredAdmins(
        (admins as OrgAdminRow[]).filter(
          (admin) =>
            admin.organization_name.toLowerCase().includes(searchLower) ||
            admin.user_name?.toLowerCase().includes(searchLower) ||
            admin.user_email?.toLowerCase().includes(searchLower) ||
            admin.domains?.some(d => d.domain.toLowerCase().includes(searchLower))
        )
      );
    }
  }, [searchTerm, admins]);

  // Handle delete
  const handleDelete = async () => {
    if (!adminToDelete) return;

    setDeleteLoading(true);
    const success = await deleteAdmin(Number(adminToDelete.id));
    setDeleteLoading(false);

    if (success) {
      setDeleteDialog(false);
      setAdminToDelete(null);
    }
  };

  // Open create page
  const handleOpenCreate = () => {
    navigate('/manage-organizational-admins/create');
  };

  // Open view page
  const handleOpenView = (admin: OrgAdminRow) => {
    navigate(`/manage-organizational-admins/view/${admin.id}`, {
      state: { admin }
    });
  };

  // Open edit page
  const handleOpenEdit = (admin: OrgAdminRow) => {
    navigate(`/manage-organizational-admins/edit/${admin.id}`, {
      state: { admin }
    });
  };

  // Open delete dialog
  const handleOpenDelete = (admin: OrgAdminRow) => {
    setAdminToDelete(admin);
    setDeleteDialog(true);
  };

  // Show message modal when success or error occurs
  useEffect(() => {
    if (success || error) {
      setMessageDialog(true);
    }
  }, [success, error]);

  // Handle message modal close
  const handleMessageDialogClose = () => {
    setMessageDialog(false);
    clearMessages();
  };

  // Table columns
  const columns: ResponsiveTableColumn[] = [
    {
      id: 'organization_name',
      label: 'Organization',
      minWidth: 150,
      format: (value: any, row: any) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Business sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={600}>
            {row.organization_name}
          </Typography>
        </Box>
      )
    },
    {
      id: 'user_name',
      label: 'Admin User',
      minWidth: 150,
      format: (value: any, row: any) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {row.user_name || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.user_email || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      id: 'domains',
      label: 'Domains',
      minWidth: 200,
      format: (value: any, row: any) => (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {row.domains && row.domains.length > 0 ? (
            row.domains.map((domain: any, idx: number) => (
              <Chip
                key={idx}
                label={domain.domain}
                size="small"
                color="info"
                variant="filled"
                sx={{
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    px: 1.5
                  }
                }}
              />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              No domains
            </Typography>
          )}
        </Box>
      )
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      format: (value: any, row: any) => (
        <Chip
          label={row.status}
          size="small"
          color={row.status === 'active' ? 'success' : 'default'}
        />
      )
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'right',
      format: (value: any, row: any) => (
        <Box display="flex" gap={1} justifyContent="flex-end">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenView(row);
            }}
            sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.info.main }}
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEdit(row);
            }}
            sx={{ color: isDarkMode ? theme.palette.warning.light : theme.palette.primary.main }}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDelete(row);
            }}
            sx={{ color: isDarkMode ? theme.palette.error.light : theme.palette.error.main }}
            title="Delete"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <AdminPanelSettings
              sx={{ fontSize: 32, color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }}
            />
            <Typography variant="h4" fontWeight={600}>
              Manage Organizational Admins
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
              onClick={fetchAllAdmins}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCreate}
              disabled={loading}
              sx={{
                bgcolor: isDarkMode ? theme.palette.info.main : theme.palette.primary.main,
                '&:hover': {
                  bgcolor: isDarkMode ? theme.palette.info.dark : theme.palette.primary.dark,
                },
                fontWeight: 600
              }}
            >
              Add Admin
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Search by organization, user, or domain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </CardContent>
        </Card>

        {/* Table */}
        {loading && !admins.length ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveTable
            columns={columns}
            rows={filteredAdmins}
            emptyMessage="No organizational admins found"
            loading={loading}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove <strong>{adminToDelete?.organization_name}</strong> as an organizational admin?
              This will also remove their admin role if they have no other admin assignments.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={deleteLoading}
              startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Message Dialog */}
        <Dialog
          open={messageDialog}
          onClose={handleMessageDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            {success && (
              <>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Success!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {success}
                </Typography>
              </>
            )}
            {error && (
              <>
                <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Error
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {error}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              variant="contained"
              onClick={handleMessageDialogClose}
              sx={{ minWidth: 120 }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default ManageOrganizationalAdmins;
